import { useState, useEffect, useRef } from 'react';
import db from '@/db';
import { useAuth } from '@/context/AuthContext';
import { simulateScaleReading, connectScale, createScaleReader, connectScaleEthernet, probeScaleBridge } from '@/lib/scale';
import { appendWeightToCommand } from '@/lib/commands';
import {
  previewCommandTicket,
} from '@/lib/printer';
import { printCommandTicket } from '@/lib/printSmart';
import { formatCurrency } from '@/lib/formatters';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Scale, Plug, Unplug, RefreshCw, Receipt, Printer, FileText, Network, Usb } from 'lucide-react';
import { toast } from 'sonner';

export default function ScalePage() {
  const { operator } = useAuth();
  const [connected, setConnected] = useState(false);
  const [reading, setReading] = useState(null);
  const [pricePerKg, setPricePerKg] = useState('');
  const [commandNumber, setCommandNumber] = useState('');
  const [readerRef, setReaderRef] = useState(null);
  const [lastCommand, setLastCommand] = useState(null);
  const [preview, setPreview] = useState(null);
  // Fase 28 — modo de conexão da balança
  const [scaleMode, setScaleMode] = useState('serial'); // 'serial' | 'ethernet'
  const [bridgeUrl, setBridgeUrl] = useState('ws://localhost:8765');
  const ethernetClientRef = useRef(null);

  useEffect(() => {
    db.config.get('buffetPricePerKg').then((bp) => {
      if (bp) setPricePerKg(String(bp.value));
    });
    // Lê modo persistido em /config
    db.config.get('scaleMode').then((m) => {
      if (m?.value === 'ethernet') setScaleMode('ethernet');
    });
    db.config.get('scaleBridgeUrl').then((u) => {
      if (u?.value) setBridgeUrl(u.value);
    });
  }, []);

  // Cleanup ethernet ao sair
  useEffect(() => () => {
    if (ethernetClientRef.current) { try { ethernetClientRef.current.close(); } catch {} }
  }, []);

  const handleConnect = async () => {
    try {
      if (scaleMode === 'ethernet') {
        // Verifica se a bridge está rodando antes de tentar WS
        const probe = await probeScaleBridge(bridgeUrl.replace(/^ws/, 'http') + '/health');
        if (!probe.ok) {
          toast.error('Bridge offline', {
            description: 'Rode o script kaven-scale-bridge.js no PC da loja antes de conectar.',
          });
          return;
        }
        const client = connectScaleEthernet({
          url: bridgeUrl,
          onMessage: (msg) => {
            if (msg.type === 'weight') {
              setReading({
                valid: true,
                weight: msg.weight,
                unit: 'kg',
                stable: !!msg.stable,
                raw: msg.raw,
              });
            } else if (msg.type === 'status') {
              setConnected(!!msg.connected);
            }
          },
          onConnectionChange: (open) => {
            // open = WS está aberto. msg.status nos dirá se a balança TCP responde.
            if (!open) setConnected(false);
          },
        });
        ethernetClientRef.current = client;
        setConnected(true); // WS aberto → status real virá pelo onMessage
        toast.success('Conectado à bridge', { description: bridgeUrl });
      } else {
        const port = await connectScale();
        const reader = createScaleReader(port, (data) => setReading(data));
        setReaderRef(reader);
        setConnected(true);
        toast.success('Balança conectada (Web Serial)!');
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao conectar balança');
    }
  };

  const handleDisconnect = async () => {
    if (readerRef) { await readerRef.stop(); setReaderRef(null); }
    if (ethernetClientRef.current) { try { ethernetClientRef.current.close(); } catch {} ethernetClientRef.current = null; }
    setConnected(false); setReading(null);
    toast.success('Balança desconectada');
  };

  const handleScaleModeChange = async (mode) => {
    // Se trocar modo enquanto conectado, desconecta primeiro
    if (connected) await handleDisconnect();
    setScaleMode(mode);
    await db.config.put({ key: 'scaleMode', value: mode });
    toast.success(`Modo: ${mode === 'ethernet' ? 'Ethernet/IP (bridge local)' : 'USB/Serial (Web Serial)'}`);
  };

  const handleBridgeUrlSave = async () => {
    await db.config.put({ key: 'scaleBridgeUrl', value: bridgeUrl });
    toast.success('URL da bridge salva');
  };

  const handleSimulate = () => setReading(simulateScaleReading());

  const handleRegisterAndPrint = async () => {
    if (!reading || !pricePerKg) return toast.error('Faça uma leitura e informe preço/kg');
    if (!commandNumber) return toast.error('Informe o número da comanda');
    const num = Number(commandNumber);
    // Pré-check: se comanda já está fechada/paga, bloqueia antes do append
    const existing = await db.commands.where('number').equals(num).first();
    if (existing && existing.status !== 'open') {
      toast.error(`Comanda #${num} já foi ${existing.status === 'paid' ? 'paga' : 'fechada'}. Use outro número.`);
      return;
    }
    const price = parseFloat(pricePerKg);
    try {
      // Cria ou reabre a comanda e adiciona a pesagem
      const cmd = await appendWeightToCommand(Number(commandNumber), reading.weight, price, operator?.id);
      setLastCommand(cmd);
      const lastEntry = cmd.weightEntries[cmd.weightEntries.length - 1];
      toast.success(`Comanda #${cmd.number}: +${reading.weight.toFixed(3)}kg = ${formatCurrency(lastEntry.subtotal)}`);

      // Tenta imprimir direto (modo Windows=HTML ou WebSerial=ESC/POS).
      // Se falhar, avisa mas comanda já está salva.
      try {
        const r = await printCommandTicket(cmd, lastEntry);
        toast.success('Cupom enviado para impressão', {
          description: r.mode === 'windows' ? 'Confira o diálogo do sistema' : 'Via Jetway (ESC/POS)',
        });
      } catch {
        toast.message('Comanda salva. Impressora não conectada.', {
          description: 'Use "Preview" para ver o cupom.',
        });
      }

      // Limpa leitura para próxima, mantém número da comanda
      setReading(null);
    } catch (err) {
      if (err?.code === 'NO_OPEN_SESSION') {
        toast.error('Caixa fechado', {
          description: 'Abra o caixa antes de criar comandas novas. Vá em Caixa e clique em "Abrir caixa".',
          duration: 6000,
        });
      } else {
        toast.error(err.message || 'Falha ao registrar');
      }
    }
  };

  const handlePreview = () => {
    if (!lastCommand) return;
    const lastEntry = lastCommand.weightEntries[lastCommand.weightEntries.length - 1];
    setPreview(previewCommandTicket(lastCommand, lastEntry));
  };

  const calculatedTotal = reading && pricePerKg ? reading.weight * parseFloat(pricePerKg || 0) : 0;

  return (
    <div data-testid="scale-page" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-3xl tracking-tight font-bold text-slate-900">Balança</h2>
          <p className="text-sm text-slate-500 mt-1">Ramuza Atena II → Jetway JP-500 automático</p>
        </div>
        <Badge className={connected ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}>
          {connected ? 'Balança Conectada' : 'Balança Desconectada'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fase 28 — Seleção de modo de conexão da balança */}
        <div className="lg:col-span-2 bg-white border-2 border-slate-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">
            Modo de Conexão
          </p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <button
              data-testid="scale-mode-serial"
              onClick={() => handleScaleModeChange('serial')}
              className={`flex flex-col items-center gap-1 p-4 rounded-lg border-2 transition-all ${
                scaleMode === 'serial'
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'
              }`}
            >
              <Usb size={22} strokeWidth={2} />
              <span className="text-xs font-bold">USB / Serial</span>
              <span className="text-[10px] text-slate-500 text-center leading-tight">Web Serial · porta COM</span>
            </button>
            <button
              data-testid="scale-mode-ethernet"
              onClick={() => handleScaleModeChange('ethernet')}
              className={`flex flex-col items-center gap-1 p-4 rounded-lg border-2 transition-all ${
                scaleMode === 'ethernet'
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'
              }`}
            >
              <Network size={22} strokeWidth={2} />
              <span className="text-xs font-bold">Ethernet / IP</span>
              <span className="text-[10px] text-slate-500 text-center leading-tight">via bridge local TCP</span>
            </button>
          </div>
          {scaleMode === 'ethernet' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
              <p className="text-[11px] font-bold text-amber-900 mb-2">
                ⚠️ Modo Ethernet exige a bridge local rodando no PC
              </p>
              <p className="text-[10px] text-amber-700 leading-relaxed mb-2">
                1. No PC da loja, rode: <code className="bg-amber-100 px-1.5 py-0.5 rounded">node kaven-scale-bridge.js --scale 192.168.18.1:4001</code><br/>
                2. Mantenha o terminal aberto (ou crie atalho na inicialização)<br/>
                3. A bridge converte TCP da Ramuza em WebSocket que o navegador consegue ler.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  data-testid="scale-bridge-url"
                  value={bridgeUrl}
                  onChange={(e) => setBridgeUrl(e.target.value)}
                  placeholder="ws://localhost:8765"
                  className="h-9 text-xs font-mono bg-white"
                />
                <button
                  data-testid="scale-bridge-save"
                  onClick={handleBridgeUrlSave}
                  className="shrink-0 h-9 px-3 rounded-lg bg-amber-700 text-white text-xs font-bold hover:bg-amber-800"
                >
                  Salvar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Weight Display */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-4">Peso Atual</p>
          <div data-testid="scale-weight-display" className="text-center py-6">
            {reading ? (
              <>
                <p className="font-mono text-6xl font-black text-slate-900 tracking-tighter">{reading.weight.toFixed(3)}</p>
                <p className="text-xl text-slate-500 font-bold mt-2">kg</p>
                {reading.stable && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mt-3">Estável</Badge>}
              </>
            ) : (
              <div className="text-slate-300">
                <Scale size={64} className="mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-lg">Aguardando leitura...</p>
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-4">
            {!connected ? (
              <button data-testid="connect-scale-btn" onClick={handleConnect} className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-all py-4 min-h-[48px]">
                <Plug size={18} strokeWidth={1.75} /> Conectar
              </button>
            ) : (
              <button data-testid="disconnect-scale-btn" onClick={handleDisconnect} className="flex-1 flex items-center justify-center gap-2 bg-white text-slate-900 rounded-lg font-bold border border-slate-300 hover:border-slate-900 transition-all py-4 min-h-[48px]">
                <Unplug size={18} strokeWidth={1.75} /> Desconectar
              </button>
            )}
            <button data-testid="simulate-scale-btn" onClick={handleSimulate} className="flex-1 flex items-center justify-center gap-2 bg-white text-slate-900 rounded-lg font-bold border border-slate-300 hover:border-slate-900 transition-all py-4 min-h-[48px]">
              <RefreshCw size={18} strokeWidth={1.75} /> Simular
            </button>
          </div>
        </div>

        {/* Command + Price */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-4">Registrar na Comanda</p>

          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-1 block">Número da Comanda</label>
              <Input
                data-testid="scale-command-input"
                type="number"
                min="1"
                value={commandNumber}
                onChange={(e) => setCommandNumber(e.target.value)}
                placeholder="Ex: 42"
                className="h-14 border border-slate-200 rounded-xl text-2xl font-mono text-center font-bold"
              />
              <p className="text-[11px] text-slate-500 mt-1">Se já existir, o novo peso será somado.</p>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-1 block">Preço por Kg (R$)</label>
              <Input
                data-testid="price-per-kg-input"
                type="number" step="0.01" min="0"
                value={pricePerKg}
                onChange={(e) => setPricePerKg(e.target.value)}
                placeholder="0.00"
                className="h-14 border border-slate-200 rounded-xl text-xl font-mono text-right"
              />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Subtotal desta pesagem</p>
              <p data-testid="scale-calculated-total" className="font-mono text-4xl font-black text-slate-900 tracking-tighter">
                {formatCurrency(calculatedTotal)}
              </p>
            </div>

            <button
              data-testid="register-weight-btn"
              onClick={handleRegisterAndPrint}
              disabled={!reading || !pricePerKg || !commandNumber}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-all py-4 min-h-[52px] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Printer size={18} strokeWidth={1.75} /> Registrar e Imprimir Jetway
            </button>
          </div>
        </div>
      </div>

      {/* Last command summary */}
      {lastCommand && (
        <div data-testid="last-command-summary" className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Receipt size={18} className="text-blue-600" strokeWidth={1.75} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Última pesagem registrada</p>
                <p className="font-heading text-xl font-bold text-slate-900">Comanda #{lastCommand.number}</p>
              </div>
            </div>
            <button data-testid="scale-preview-btn" onClick={handlePreview} className="flex items-center gap-1.5 px-4 h-10 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">
              <FileText size={14} /> Preview Cupom
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Pesagens</p>
              <p className="font-mono text-lg font-black text-slate-900">{lastCommand.weightEntries?.length || 0}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Buffet</p>
              <p className="font-mono text-lg font-black text-slate-900">{formatCurrency(lastCommand.buffetTotal || 0)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Bebidas</p>
              <p className="font-mono text-lg font-black text-slate-900">{formatCurrency(lastCommand.itemsTotal || 0)}</p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-[9px] uppercase tracking-wider font-bold text-blue-600">Total</p>
              <p className="font-mono text-lg font-black text-blue-700">{formatCurrency(lastCommand.total || 0)}</p>
            </div>
          </div>
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-md bg-white border border-slate-200 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Preview Cupom da Balança</DialogTitle>
          </DialogHeader>
          <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-[11px] font-mono overflow-x-auto whitespace-pre leading-relaxed max-h-96">
            {preview}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
